import { API_BASE_URL } from "src/config/apiBase";
import React, { useState } from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";
import ReminderPicker from "../../shared/tasks/components/ReminderPicker";
import RepeatAdvancedFields from "../../shared/tasks/components/RepeatAdvancedFields";
import { appendReminderFields } from "../../shared/tasks/utils/taskReminderSync";

const TaskModal = ({ isOpen, setIsOpen, leadOptions, assignOptions, fetchTask }) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const [dueDate, setDueDate] = useState(tomorrow);
    const [startTime, setStartTime] = useState();
    const [startDate, setStartDate] = useState(new Date());
    const [repetition, setRepetition] = useState(null);
    const [customDate, setCustomDate] = useState(null);
    const [title, setTitle] = useState("");
    const [priority, setPriority] = useState(null);
    const [assignTo, setAssignTo] = useState(null);
    const [list, setList] = useState(null);
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [reminderPreset, setReminderPreset] = useState("none");
    const [reminderCustomMinutes, setReminderCustomMinutes] = useState(15);
    const [repeatInterval, setRepeatInterval] = useState(1);
    const [repeatDays, setRepeatDays] = useState([]);
    const [repeatEndType, setRepeatEndType] = useState("never");
    const [repeatEndDate, setRepeatEndDate] = useState("");

    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";

    const repetitionOptions = [
        { value: "none", label: "Does not repeat" },
        { value: "daily", label: "Daily" },
        { value: "weekdays", label: "Weekdays" },
        { value: "weekly", label: "Weekly" },
        { value: "monthly", label: "Monthly" },
        { value: "annually", label: "Yearly" },
        { value: "custom", label: "Custom" },
    ];

    const priorityOptions = [
        { value: "high", label: "High" },
        { value: "medium", label: "Medium" },
        { value: "low", label: "Low" },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim()) return toast.error("Title is required");
        if (!assignTo && assignOptions.length > 0) return toast.error("Assign To is required");

        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("due_date", dueDate || new Date());
            formData.append("priority", priority?.value || "medium");
            formData.append("repetition", repetition?.value || "none");
            if (repetition?.value === "custom") {
                formData.append("customDate", customDate);
            }
            formData.append("assign_to", assignTo?.value || "");
            formData.append("list_id", list?.value || "");
            formData.append("notes", notes || "");
            formData.append("start_time", startTime || "09:00");
            formData.append("start_date", startDate || dueDate || new Date());
            appendReminderFields(formData, reminderPreset, reminderCustomMinutes);
            if (repeatInterval > 1) formData.append("repeat_interval", String(repeatInterval));
            if (repeatDays.length) formData.append("repeat_days", JSON.stringify(repeatDays));
            if (repeatEndType) formData.append("repeat_end_type", repeatEndType);
            if (repeatEndDate) formData.append("repeat_end_date", repeatEndDate);
            if (file) {
                formData.append("attachment", file);
            }

            const response = await fetch(`${url}/task`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            const res = await response.json();
            if (response.ok) {
                toast.success("Task created successfully!");
                setTitle("");
                setDueDate("");
                setRepetition("");
                setCustomDate("");
                setPriority("");
                setAssignTo("");
                setList("");
                setNotes("");
                setFile("");
                setStartTime("");
                fetchTask();
                setIsOpen(false);
            } else {
                toast.error(res.error || "Failed to create task");
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="task-modal-overlay"
            onClick={() => setIsOpen(false)}
        >
            <div
                className="task-modal-panel"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="oms-modal-title">Create New Task</h2>

                <form className="oms-modal-form" onSubmit={handleSubmit}>
                    <div className="oms-form-group">
                        <label htmlFor="task-title" className="oms-form-label">
                            Title
                        </label>
                        <input
                            id="task-title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter task title"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="oms-form-group">
                            <label className="oms-form-label">Start Date</label>
                            <Flatpickr
                                value={startDate}
                                options={{ minDate: "today" }}
                                onChange={(date) => setStartDate(date[0])}
                            />
                        </div>
                        <div className="oms-form-group">
                            <label className="oms-form-label">Due Date</label>
                            <Flatpickr
                                value={dueDate}
                                options={{ minDate: "today" }}
                                onChange={(date) => setDueDate(date[0])}
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Priority</label>
                            <Select
                                options={priorityOptions}
                                value={priority}
                                onChange={setPriority}
                                placeholder="Select priority"
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Due Time</label>
                            <Flatpickr
                                options={{
                                    enableTime: true,
                                    noCalendar: true,
                                    dateFormat: "H:i",
                                    time_24hr: true,
                                    defaultDate: "09:00",
                                }}
                                value={startTime}
                                onChange={(date, dateStr) => setStartTime(dateStr)}
                                placeholder="Select due time"
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Reminder</label>
                            <ReminderPicker
                                value={reminderPreset}
                                customMinutes={reminderCustomMinutes}
                                onChange={({ preset, customMinutes }) => {
                                    setReminderPreset(preset);
                                    if (customMinutes) setReminderCustomMinutes(customMinutes);
                                }}
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Repetition</label>
                            <Select
                                options={repetitionOptions}
                                value={repetition}
                                onChange={setRepetition}
                                placeholder="Select repetition"
                            />

                            {repetition?.value === "custom" && (
                                <Flatpickr
                                    value={customDate}
                                    options={{ minDate: "today" }}
                                    onChange={(date) => setCustomDate(date[0])}
                                    placeholder="Pick custom date"
                                />
                            )}

                            {repetition?.value && repetition.value !== "none" && (
                                <RepeatAdvancedFields
                                    repetition={repetition.value}
                                    repeatInterval={repeatInterval}
                                    repeatDays={repeatDays}
                                    repeatEndType={repeatEndType}
                                    repeatEndDate={repeatEndDate}
                                    onChange={(patch) => {
                                        if (patch.repeat_interval != null) setRepeatInterval(patch.repeat_interval);
                                        if (patch.repeat_days) setRepeatDays(patch.repeat_days);
                                        if (patch.repeat_end_type) setRepeatEndType(patch.repeat_end_type);
                                        if (patch.repeat_end_date != null) setRepeatEndDate(patch.repeat_end_date);
                                    }}
                                />
                            )}
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Assign To</label>
                            <Select
                                options={assignOptions}
                                value={assignTo}
                                onChange={setAssignTo}
                                placeholder="Select user"
                                getOptionValue={(option) => option.value}
                            />
                        </div>

                        <div className="oms-form-group">
                            <label className="oms-form-label">Add to List</label>
                            <Select
                                options={leadOptions}
                                value={list}
                                onChange={setList}
                                placeholder="Select list"
                                getOptionLabel={(option) => (
                                    <div className="flex items-center">
                                        <span
                                            className="inline-block w-3 h-3 rounded-full mr-2 shrink-0"
                                            style={{ backgroundColor: option.color }}
                                        />
                                        {option.label}
                                    </div>
                                )}
                                getOptionValue={(option) => option.value}
                            />
                        </div>
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Enter notes"
                            rows={3}
                        />
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Attachment</label>
                        <input type="file" onChange={(e) => setFile(e.target.files[0])} />
                    </div>

                    <div className="task-modal-actions">
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="task-modal-btn task-modal-btn--ghost"
                        >
                            Cancel
                        </button>
                        <button type="submit" className="task-modal-btn task-modal-btn--primary">
                            Create Task
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskModal;
